import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createUserWithEmailAndPassword, EmailAuthProvider, GoogleAuthProvider, linkWithCredential, linkWithPopup, onAuthStateChanged, sendPasswordResetEmail, signInAnonymously, signInWithEmailAndPassword, signInWithPopup, signOut, updateProfile, type User } from 'firebase/auth'
import { communityApi, communityError, type EngagementSummary } from './api'
import { firebaseAuth } from '../services/firebase'
import { trackEvent } from '../services/analytics'
import { AuthModal } from './AuthModal'

type AuthIntent = 'pulse' | 'report' | 'comment' | 'reply' | 'account'
interface CommunityContextValue {
  user: User | null
  authLoading: boolean
  summaries: Record<string, EngagementSummary>
  ensureSignal: (signalId: string) => void
  refreshSignal: (signalId: string) => Promise<void>
  requireAuth: (intent: AuthIntent, action: () => void | Promise<void>) => void
  openAccount: () => void
  logOut: () => Promise<void>
}

const CommunityContext = createContext<CommunityContextValue | null>(null)
const emptySummary = { pulseCount: 0, commentCount: 0, pulsed: false }

export function CommunityProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [intent, setIntent] = useState<AuthIntent>('account')
  const [summaries, setSummaries] = useState<Record<string, EngagementSummary>>({})
  const pendingAction = useRef<(() => void | Promise<void>) | null>(null)
  const queued = useRef(new Set<string>())
  const timer = useRef<number | undefined>(undefined)

  const loadBatch = useCallback(async (ids: string[]) => {
    if (!ids.length) return
    try {
      const response = await communityApi.getBatch(ids)
      setSummaries((current) => ({ ...current, ...response.signals }))
    } catch { ids.forEach((id) => queued.current.delete(id)) }
  }, [])

  const flush = useCallback(async () => {
    const ids = [...queued.current]
    queued.current.clear()
    for (let index = 0; index < ids.length; index += 50) await loadBatch(ids.slice(index, index + 50))
  }, [loadBatch])

  const ensureSignal = useCallback((signalId: string) => {
    if (summaries[signalId] || queued.current.has(signalId)) return
    queued.current.add(signalId)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void flush(), 45)
  }, [flush, summaries])

  const refreshSignal = useCallback(async (signalId: string) => { await loadBatch([signalId]) }, [loadBatch])

  useEffect(() => onAuthStateChanged(firebaseAuth, (nextUser) => { setUser(nextUser); setAuthLoading(false); setSummaries({}); queued.current.clear() }), [])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const requireAuth = useCallback((nextIntent: AuthIntent, action: () => void | Promise<void>) => {
    if (firebaseAuth.currentUser && (nextIntent === 'pulse' || nextIntent === 'report' || !firebaseAuth.currentUser.isAnonymous)) { void action(); return }
    if (nextIntent === 'pulse' || nextIntent === 'report') {
      void signInAnonymously(firebaseAuth).then(async () => { trackEvent('anonymous_identity_create', { intent: nextIntent }); await action() }).catch(() => { pendingAction.current = action; setIntent(nextIntent); setModalOpen(true) })
      return
    }
    pendingAction.current = action
    setIntent(nextIntent)
    setModalOpen(true)
    trackEvent('auth_prompt_open', { intent: nextIntent })
  }, [])

  const completeAuth = useCallback(async (nextUser: User) => {
    setUser(nextUser)
    setModalOpen(false)
    trackEvent('auth_success', { method: nextUser.providerData[0]?.providerId ?? 'email', intent })
    const action = pendingAction.current
    pendingAction.current = null
    if (action) await action()
  }, [intent])

  const value = useMemo<CommunityContextValue>(() => ({
    user, authLoading, summaries, ensureSignal, refreshSignal, requireAuth,
    openAccount: () => { pendingAction.current = null; setIntent('account'); setModalOpen(true) },
    logOut: async () => { await signOut(firebaseAuth); trackEvent('auth_sign_out') },
  }), [user, authLoading, summaries, ensureSignal, refreshSignal, requireAuth])

  return <CommunityContext.Provider value={value}>
    {children}
    {modalOpen && <AuthModal
      user={user}
      intent={intent}
      googleEnabled={import.meta.env.VITE_FIREBASE_GOOGLE_AUTH_ENABLED === 'true'}
      onClose={() => { pendingAction.current = null; setModalOpen(false) }}
      onGoogle={async () => completeAuth((await (firebaseAuth.currentUser?.isAnonymous ? linkWithPopup(firebaseAuth.currentUser, new GoogleAuthProvider()) : signInWithPopup(firebaseAuth, new GoogleAuthProvider()))).user)}
      onEmailSignIn={async (email, password) => completeAuth((await signInWithEmailAndPassword(firebaseAuth, email, password)).user)}
      onEmailCreate={async (email, password, displayName) => { const credential = firebaseAuth.currentUser?.isAnonymous ? await linkWithCredential(firebaseAuth.currentUser, EmailAuthProvider.credential(email, password)) : await createUserWithEmailAndPassword(firebaseAuth, email, password); await updateProfile(credential.user, { displayName }); await communityApi.updateProfile(displayName, null); await completeAuth(credential.user) }}
      onReset={(email) => sendPasswordResetEmail(firebaseAuth, email)}
      onProfile={async (displayName, photoURL) => { await updateProfile(firebaseAuth.currentUser!, { displayName, photoURL }); await communityApi.updateProfile(displayName, photoURL); setUser(firebaseAuth.currentUser); setModalOpen(false); trackEvent('community_profile_update') }}
      errorText={communityError}
    />}
  </CommunityContext.Provider>
}

export function useCommunity() {
  const value = useContext(CommunityContext)
  if (!value) throw new Error('useCommunity must be used inside CommunityProvider')
  return value
}

export { emptySummary }
